import { Injectable, Injector } from '@angular/core';
import { environment, keycloakAngularConfig } from '@env/environment';
import 'whatwg-fetch';
import { Router } from '@angular/router';
import { AuthenticationService } from './core/authentication/authentication.service';
import { HttpClient } from '@angular/common/http';
import { Logger } from './core';
import { Identifiers } from './shared/services/app.config.type';
import { KeycloakService } from 'keycloak-angular';
import moment from 'moment';
import jwtDecode from 'jwt-decode';
import { NetworkConnectionService } from './core/network.service';

// Combines the Keycloak "cache_version" claim (whole realm, hardcoded claim mapper) and the optional
// "user_cache_version" claim (one user, user attribute mapper). Without the user claim the value is just
// cache_version, so users without the attribute keep the same stored version.
export function getCacheVersion(token: any): string | null {
  const realmVersion = token?.cache_version;
  const userVersion = token?.user_cache_version;
  if (realmVersion == null && userVersion == null) return null;
  if (userVersion == null) return String(realmVersion);
  return `${realmVersion ?? ''}.${userVersion}`;
}

// Public pages the cache version check leaves alone (logout, idle timeout, password reset, ...).
// Same list as allowedPaths in MyStartupService.load(); keep them in sync.
const CACHE_CHECK_SKIPPED_PATHS = [
  '/resetpassword',
  '/forgotpassword',
  '/sso',
  '/idle-timeout',
  '/session-message',
  '/signout-message',
  '/code-verification',
  '/recovery-code',
  '/recovery-code-verification',
  '/set-password',
  '/post-logout',
  '/ext-signout-callback',
];

@Injectable()
export class MyStartupService {
  public data: any;
  private authService: AuthenticationService;
  private keycloack: KeycloakService;
  private http: HttpClient;
  private networkService: NetworkConnectionService;

  constructor(private injector: Injector) {
    if (this.keycloack === undefined) {
      this.keycloack = this.injector.get(KeycloakService);
    }

    if (this.authService === undefined) {
      this.authService = this.injector.get(AuthenticationService);
    }

    if (this.http === undefined) {
      this.http = this.injector.get(HttpClient);
    }

    if (this.networkService === undefined) {
      this.networkService = this.injector.get(NetworkConnectionService);
    }

    const fire = (this.http as any)['appConfig'].getConfig(Identifiers.FrontendLogger, 'fire');
    Logger.fire = fire;
    Logger.httpClient = this.http;
    Logger.netWorkService = this.networkService;
    Logger.authService = this.authService;
  }

  async initializeKeycloak() {
    if (JSON.parse(localStorage.getItem('isSSOLogin'))) return;

    var initOptions: Keycloak.KeycloakInitOptions = {
      redirectUri: keycloakAngularConfig.redirectUri,
      // onLoad: 'check-sso',
      onLoad: 'login-required',
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri: keycloakAngularConfig.silentCheckSsoRedirectUri,
      enableLogging: keycloakAngularConfig.enableLogging,
      checkLoginIframe: keycloakAngularConfig.checkLoginIframe,
    };

    let allowedPaths = [
      'resetpassword',
      'forgotpassword',
      'sso',
      'idle-timeout',
      'session-message',
      'signout-message',
      'code-verification',
      'recovery-code',
      'recovery-code-verification',
      'set-password',
      'post-logout',
      'ext-signout-callback',
    ];

    //need login only for auth code flow or browser redirect...
    let isPathAllowed = allowedPaths.some((a) => location.pathname.includes(a));
    if (isPathAllowed) {
      initOptions.onLoad = 'check-sso'; // Don't force login
      initOptions.silentCheckSsoRedirectUri = keycloakAngularConfig.silentCheckSsoRedirectUri;
      initOptions.redirectUri = window.location.href;
      console.log(initOptions.onLoad);
    } else {
      initOptions.onLoad = 'login-required';
      initOptions.redirectUri = window.location.href;
      console.log(initOptions.onLoad);
    }
    const _config = {
      url: keycloakAngularConfig.authority,
      realm: keycloakAngularConfig.realm,
      clientId: keycloakAngularConfig.clientId,
    };

    try {
      const mtd = localStorage.getItem('gbf_date_identifier');

      if (String(mtd).toLowerCase() !== 'custom range') {
        const [start, end] = String(localStorage.getItem('gbf_calendar')).split('|');
        const currentMonth = moment().format('MMM');
        const endMonth = moment(end, 'MMM D, YYYY').format('MMM');

        if (endMonth === currentMonth) {
          let dates = [
            moment(start, 'MMM D, YYYY').startOf('month').format('MMM D, YYYY'),
            moment().format('MMM D, YYYY'),
          ];
          let strDates = dates.join('|');
          localStorage.setItem('gbf_calendar', strDates);
        }
      }

      const initaited = await this.keycloack.init({
        config: _config,
        initOptions,
        // The /account profile isn't used anywhere; the parsed token covers the "is logged in" checks.
        loadUserProfileAtStartUp: false,
      });

      return initaited;
    } catch (error) {
      console.log(error);
      window.localStorage.clear();
      window.location.reload();
    }
  }

  wait(time: number) {
    return new Promise((resolve: any) => {
      setTimeout(() => {
        resolve();
      }, time);
    });
  }

  isTokenExpired(expires_in: number): boolean {
    if (!expires_in) {
      return true;
    }

    const seconds = 1000;
    const d = new Date();
    const t = d.getTime();

    if (expires_in < Math.round(t / seconds)) {
      return true;
    }
    return false;
  }

  async load() {
    const path = location.pathname.toLowerCase();
    const allowedPaths = [
      '/resetpassword',
      '/forgotpassword',
      '/sso',
      '/idle-timeout',
      '/session-message',
      '/signout-message',
      '/code-verification',
      '/recovery-code',
      '/recovery-code-verification',
      '/set-password',
      '/post-logout',
      '/ext-signout-callback',
    ];

    if (allowedPaths.some((p) => path.includes(p))) {
      return Promise.resolve(1);
    }

    const user = await this.authService.initUser();
    const router = this.injector.get(Router);
    const iSSOLogin = this.authService.checkIsSSOLogin();

    const ls = JSON.parse(localStorage.getItem('authorizationData'));
    // if (ls === undefined || !ls || (iSSOLogin === false && (user == null || this.isTokenExpired(user.expires_at)))) {
    if (ls === undefined || !ls || (iSSOLogin === false && user == null)) {
      return new Promise((resolve, reject) => {
        resolve(1);
      });
    } else if (ls) {
      const features = ls['Features'];
      const tempclientId = ls['ClientId'];
      if (features === null || features === undefined || tempclientId === null || tempclientId === undefined) {
        localStorage.clear();
        window.location.href = '/';
        return new Promise((resolve, reject) => {
          resolve(1);
        });
      }
    }

    const clientId = ls['ClientId'];
    if (clientId !== undefined && clientId) {
      // const filterCOnfiguration = await this.getConfiguration('gf','Global Filter');
      const languageCode = localStorage.getItem('language');
      //const languageCode = ls['DefaultLanguage'];
      const marketLanguage = ls['DefaultMarketLanguage'];
      const token = this.authService.getToken();

      // const token = "77992f494f407c21c21afa39524a057f6e736966c92c28de57faf422f31c0e33";
      const headers = { 'Content-Type': 'application/json' };

      // Always honour the user's saved language; fall back to the market default only when none is stored.
      const language = languageCode || marketLanguage;
      if (token) {
        (headers as any)['Authorization'] = `Bearer ${token}`;
      }
      return new Promise((resolve, reject) => {
        let url: string;
        if (localStorage.getItem('translations')) {
          const translations = localStorage.getItem('translations');
          this.data = JSON.parse(translations);
          resolve(1);
        } else {
          if (environment.production) {
            url = `${environment.apiUrl + environment.serverUrl + environment.apiVersion}/multilingual/${language}`;
          } else {
            url = `${environment.apiUrl}/api/v1/multilingual/${language}`;
          }
          return fetch(url, { headers })
            .then((res) => {
              if (res.status === 200) {
                return res.json();
              } else if (res.status === 401) {
                window.localStorage.clear();
                window.location.reload();
                return;
              } else {
                window.location.href = 'error.html';
              }
            })
            .then((res) => {
              this.data = res;
              // localStorage.setItem('translations', JSON.stringify(res));
              localStorage.setItem('translations', JSON['makeStringify'](res));
              resolve(res);
            })
            .catch((error) => {
              window.location.href = 'error.html';
            });
        }
      });
    } else {
      return new Promise((resolve, reject) => {
        resolve(1);
      });
    }
  }

  private getConfiguration(id: string, module: string) {
    return new Promise((resolve, reject) => {
      const user = JSON.parse(localStorage.getItem('authorizationData'));
      if (!user) {
        resolve(true);
      }
      const url = `/configuration?Id=${id}&UserId=${user.Id}&ClientId=${user.ClientId}&Module=${module}`;
      return this.http
        .disableLoader()
        .disableHttpCancel()
        .get<any>(url)
        .subscribe((configuration: any) => {
          if (configuration && configuration.Settings) {
            const object = configuration.Settings;
            for (const property in object) {
              localStorage.setItem(property, object[property]);
            }
            resolve(true);
          } else {
            resolve(true);
          }
        });
    });
  }

  async clearAllBrowserStorage() {
    try {
      // 🧹 1. Clear localStorage & sessionStorage
      localStorage.clear();
      sessionStorage.clear();

      // 🍪 2. Clear cookies (non-HTTP-only only)
      const cookies = document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter(Boolean);
      const hostname = window.location.hostname;
      const domains = [hostname];
      if (hostname.split('.').length > 1) domains.push('.' + hostname);

      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        domains.forEach((domain) => {
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain};`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
        });
      });

      // 💾 3. Clear IndexedDB
      if ('indexedDB' in window) {
        if (indexedDB.databases) {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs.map((db) =>
              db.name
                ? new Promise((res: any) => {
                    const req = indexedDB.deleteDatabase(db.name);
                    req.onsuccess = req.onerror = req.onblocked = () => res();
                  })
                : Promise.resolve()
            )
          );
        }
      }

      // 📦 4. Clear Cache Storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // ⚙️ 5. Unregister Service Workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      console.log('✅ All available storage cleared!');
    } catch (err) {
      console.error('❌ Error while clearing storage:', err);
    }
  }

  async isValidUser() {
    if (await this.isCacheVersionChanged()) return;

    const token = this.authService.getToken();
    if (!token) return; //new
    const decoded: any = jwtDecode(token);
    try {
      const authData = JSON.parse(localStorage.getItem('authorizationData'));
      if (authData && decoded && authData?.Id && decoded?.UserId && String(authData?.Id) !== String(decoded?.UserId)) {
        if (String(authData?.Id).length > 0 && String(decoded?.UserId).length > 0) {
          await this.clearAllBrowserStorage();
          location.reload();
        }
      }
    } catch (error) {
      console.log('Parsing error authorizationData');
    }
  }

  // Bumping cache_version (everyone) or user_cache_version (one user) in Keycloak clears the cached data on the
  // next page load, without a logout. Legacy /sso logins don't use Keycloak init, so they're skipped.
  private async isCacheVersionChanged(): Promise<boolean> {
    if (this.authService.checkIsSSOLogin()) return false;

    const path = location.pathname.toLowerCase();
    if (CACHE_CHECK_SKIPPED_PATHS.some((p) => path.includes(p))) return false;

    const serverVersion = getCacheVersion(this.keycloack.getKeycloakInstance()?.tokenParsed);
    if (serverVersion === null) return false;

    // Nothing cached yet (fresh login): processlogin() stamps the version itself.
    if (!localStorage.getItem('authorizationData')) return false;
    if (localStorage.getItem('cache_version') === serverVersion) return false;

    const tenant = localStorage.getItem('tenant');
    await this.clearAllBrowserStorage();
    // Stamp before redirecting so the next load doesn't clear again.
    localStorage.setItem('cache_version', serverVersion);
    if (tenant) localStorage.setItem('tenant', tenant);

    // Back to the login route so processlogin() re-hydrates claims, translations and orgs.
    location.href = document.baseURI;
    return true;
  }

  isAllowedLevel() {
    if (Array.isArray(this.authService?.authData?.DataAccessLevels)) {
      const dataAccessLevel = this.authService.authData.DataAccessLevels.find(Boolean);
      const dataAccessIds = this.authService.authData.DataAccessLevels.map((x: any) => x.Id);
      const item = JSON.parse(localStorage.getItem('gbf_org'))?.selected;

      if (item && !dataAccessIds.includes(item?.Id)) {
        if (item?.level < dataAccessLevel?.Level && item?.level < dataAccessLevel?.Level) {
          this.clearOrg();
        } else if (item?.level < dataAccessLevel?.Level && this.authService.authData.DataAccessLevels.length === 1) {
          this.clearOrg();
        } else if (
          item?.level < 2 &&
          this.authService.authData.DataAccessLevels.every(
            (xitem: any) => xitem.Level === this.authService.authData?.MainNodeLevel
          )
        ) {
          this.clearOrg();
        }
      }
    }
  }

  async clearOrg() {
    localStorage.removeItem('gbf_org');
    localStorage.removeItem('gbf_org_marked');
    console.log('✅ Organization storage cleared!');
  }
}
